import React, { useState, useEffect } from "react";
import { Grid } from '@material-ui/core';
import {
  Box, Card, CardContent, FormControl, InputLabel, Alert, AlertTitle,
  Button, Dialog, Select, MenuItem, Chip, OutlinedInput, Checkbox, ListItemText
} from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import MainCard from '../components/MainCard';
import StickyHeadTwoButtonTable from '../components/StickyHeadTwoButtonTable';
import StickyHeadCombineRowTable from '../components/Table/StickyHeadCombineRowTable';
import LineChart from '../components/Chart/LineChart';
import axios from "../../components/axios";
import { useNavigate } from 'react-router-dom';

const styleButton = {
  backgroundColor: "#406599",
  color: "#fff",
  borderRadius: "5px",
  boxSizing: "border-box",
  width: "100px",
  height: "45px",
  fontFamily: '"Roboto", "sans-serif"',
  fontSize: "15px",
};

const styleField = { width: '100%' };

const buildTypeOption = [
  { value: 'external' },
  { value: 'user' },
  { value: 'userdebug' },
  { value: 'signed' },
];

const second_columns = [
  { id: 'build_number', label: 'Build Number',   align: 'left' },
  { id: 'is_2G',        label: 'RAM config',     align: 'left' },
  { id: 'device_sn',    label: 'Device SN',      align: 'left' },
  { id: 'suite_name',   label: 'Suite Name',      align: 'left'},
  { id: 'count_1',      label: 'Count 1',         align: 'left' },
  { id: 'count_2',      label: 'Count 2',         align: 'left' },
  { id: 'count_3',      label: 'Count 3',         align: 'left' },
  { id: 'count_4',      label: 'Count 4',         align: 'left' },
  { id: 'count_5',      label: 'Count 5',         align: 'left' },
  { id: 'average',      label: 'Average (s)',     align: 'left' },
];

const PerformanceHistoryPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [isBootTime, setIsBootTime] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [noDataMessage, setNoDataMessage] = useState('');
  const [openSummary, setOpenSummary] = useState(false);
  const [series, setSeries] = useState([]);
  const [series2G, setSeries2G] = useState([]);
  const [category, setCategory] = useState([]);
  const [category2G, setCategory2G] = useState([]);
  const [decimal, setDecimal] = useState(5);
  const [lineWidth, setLineWidth] = useState([]);
  const [lineDash, setLineDash] = useState([]);
  const [showSummary] = useState(true);

  const [projectList, setProjectList] = useState([]);
  const [project, setProject] = useState("");
  const [buildType, setBuildType] = useState("");
  const [testSuite, setTestSuite] = useState("");
  const [testSuiteList, setTestSuiteList] = useState([]);

  const [snOptions, setSnOptions] = useState([]);
  const [selectedSNs, setSelectedSNs] = useState([]);

  function handleProject(e) {
    setProject(e.target.value);
    setTestSuite("");
    setTestSuiteList([]);
    setSelectedSNs([]);
    setSnOptions([]);
  }

  function handleBuildType(e) {
    setBuildType(e.target.value);
    setTestSuite("");
    getSuiteOptions(e.target.value);
    setSelectedSNs([]);
    setSnOptions([]);
  }

  function handleTestSuite(e) {
    setTestSuite(e.target.value);
    setSelectedSNs([]);
    setSnOptions([]);
  }

  function handleSNChange(e) {
    const val = e.target.value;
    setSelectedSNs(typeof val === 'string' ? val.split(',') : val);
  }

  const getProjectOptions = async () => {
    const res = await axios.post("/peloton/v1/automation/performance-options",
      JSON.stringify({ target: "project" }), { withCredentials: true });
    setProjectList(res.data.project_options || []);
  };

  const getSuiteOptions = async (bt) => {
    const res = await axios.post("/peloton/v1/automation/performance-options",
      JSON.stringify({ target: "buildType", build_type: bt }), { withCredentials: true });
    setTestSuiteList(res.data.type_options || []);
  };

  useEffect(() => { getProjectOptions(); }, []);

  const handleClose = () => { setOpenSummary(false); };

  const handleQueryData = () => {
    if (!project || !testSuite || !buildType) {
      alert("Please select project, build type and test suite!");
      return;
    }
    setSeries([]);
    setSeries2G([]);
    setCategory([]);
    setCategory2G([]);
    setRows([]);
    setColumns([]);
    setSnOptions([]);
    setSelectedSNs([]);
    setShowTable(false);
    setShowAlert(false);

    const req = JSON.stringify({ project, test_suite: testSuite, build_type: buildType });

    axios.post("/peloton/v1/automation/performance-history-sn-options", req, { withCredentials: true })
      .then(res => { setSnOptions(res.data.data || []); })
      .catch(() => {});

    axios.post("/peloton/v1/automation/performance-history-report", req, { withCredentials: true })
      .then(res => {
        if (res.data.message === "success" && res.data.data && res.data.data.length > 0) {
          applyResponse(res.data, []);
        } else {
          setNoDataMessage('This condition has no performance history data.');
          setShowAlert(true);
          setShowTable(false);
        }
      })
      .catch(err => handleError(err));
  };

  const applyResponse = (data, activeSNs) => {
    const suiteIsBootTime = testSuite === "BootTime";
    setIsBootTime(suiteIsBootTime);

    const allCategory = data.chart_category || [];

    if (activeSNs.length > 0 && suiteIsBootTime) {
      // per-SN mode: chart_category is 4G only, chart_series is one per SN
      setCategory(allCategory);
      setCategory2G([]);
      setSeries(data.chart_series || []);
      setSeries2G([]);
      setLineWidth((data.chart_series || []).map(() => 3));
      setLineDash((data.chart_series || []).map(() => 0));
      setDecimal(5);
    } else {
      const cat4G = allCategory.filter(c => !c.endsWith(' (2G)'));
      const cat2G = allCategory.filter(c => c.endsWith(' (2G)')).map(c => c.replace(' (2G)', ''));
      setCategory(cat4G);
      setCategory2G(cat2G);
      if (suiteIsBootTime) {
        setLineWidth([5, 5]);
        setLineDash([0, 0]);
        setDecimal(5);
      } else if (testSuite === "RAM") {
        setDecimal(0);
        setLineWidth([5]);
        setLineDash([0]);
      } else {
        setDecimal(5);
        setLineWidth([5]);
        setLineDash([0]);
      }
      setSeries(data.chart_series || []);
      if (data.chart_series_2g && data.chart_series_2g.length > 0) {
        setSeries2G(data.chart_series_2g);
      }
    }

    setColumns(data.columns || []);
    const displayRows = (data.data || []).map(row => ({
      ...row,
      is_2G: row.is_2G === 'y' ? '2G' : (row.is_2G === 'n' ? '4G' : row.is_2G),
    }));
    setRows(displayRows);
    setShowAlert(false);
    setShowTable(true);
  };

  const handleSummary = () => {
    if (selectedSNs.length === 0) {
      setOpenSummary(true);
      return;
    }
    // Fetch per-SN chart then update (dialog stays open if already open)
    const req = JSON.stringify({
      project, test_suite: testSuite, build_type: buildType,
      device_sns: selectedSNs,
    });
    axios.post("/peloton/v1/automation/performance-history-report", req, { withCredentials: true })
      .then(res => {
        if (res.data.message === "success") {
          applyResponse(res.data, selectedSNs);
          setOpenSummary(true);
        } else {
          alert(res.data.message);
        }
      })
      .catch(err => handleError(err));
  };

  const handleError = (err) => {
    if (!err.response) {
      alert(err);
    } else if (err.response.status === 401) {
      navigate("/");
    } else {
      alert(err.response.data.message);
    }
  };

  return (
    <MainCard title="Automation Reports">
      <Card variant="outlined" sx={{ bgcolor: "white" }}>
        <CardContent>
          <Box sx={{ fontSize: '20px', color: "#406599", pb: '15px' }}>
            Performance History
          </Box>
          <Grid container direction="row" justifyContent="space-around" alignItems="center">

            <Grid item xs={1}>
              <FormControl style={styleField}>
                <InputLabel>Select Project</InputLabel>
                <Select fullWidth value={project} onChange={handleProject} variant="filled">
                  {projectList.map(o => <MenuItem key={o.value} value={o.value}>{o.value}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={2}>
              <FormControl style={styleField}>
                <InputLabel>Select Build Type</InputLabel>
                <Select fullWidth value={buildType} onChange={handleBuildType} variant="filled">
                  {buildTypeOption.map(o => <MenuItem key={o.value} value={o.value}>{o.value}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={2}>
              <FormControl style={styleField}>
                <InputLabel>Select Suite</InputLabel>
                <Select fullWidth value={testSuite} onChange={handleTestSuite} variant="filled">
                  {testSuiteList.map(o => <MenuItem key={o.value} value={o.value}>{o.value}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={1}>
              <Dialog open={openSummary} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
                <DialogContent dividers>
                  {snOptions.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <FormControl sx={{ flex: 1 }}>
                        <InputLabel>Filter Device SN</InputLabel>
                        <Select
                          multiple
                          value={selectedSNs}
                          onChange={handleSNChange}
                          input={<OutlinedInput label="Filter Device SN" />}
                          renderValue={selected => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map(v => <Chip key={v} label={v} size="small" />)}
                            </Box>
                          )}
                        >
                          {snOptions.map(sn => (
                            <MenuItem key={sn} value={sn}>
                              <Checkbox checked={selectedSNs.indexOf(sn) > -1} />
                              <ListItemText primary={sn} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button style={styleButton} onClick={handleSummary}>Apply</Button>
                    </Box>
                  )}
                  <LineChart
                    isLoading={false}
                    categories={category}
                    series={series}
                    decimal={decimal}
                    lineWidth={lineWidth.length > 0 ? lineWidth : [3]}
                    lineDash={lineDash.length > 0 ? lineDash : [0]}
                    title={selectedSNs.length > 0 ? "Per-SN Boot Speed" : ""}
                    series2G={series2G}
                    categories2G={category2G}
                  />
                </DialogContent>
              </Dialog>
            </Grid>

            <Grid item xs={1}>
              <Button style={styleButton} onClick={handleQueryData}>Search</Button>
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {showTable && !isBootTime && (
        <StickyHeadTwoButtonTable
          rows={rows}
          columns={columns}
          showSummary={showSummary}
          handleSummary={handleSummary}
          handleDelete={() => {}}
        />
      )}
      {showTable && isBootTime && (
        <StickyHeadCombineRowTable
          rows={rows}
          columns={second_columns}
          showSummary={showSummary}
          handleSummary={handleSummary}
          handleDelete={() => {}}
        />
      )}
      {showAlert && (
        <Card variant="outlined" sx={{ bgcolor: "white" }}>
          <Alert severity="warning">
            <AlertTitle>Warning</AlertTitle>
            {noDataMessage}
          </Alert>
        </Card>
      )}
    </MainCard>
  );
};

export default PerformanceHistoryPage;
